import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, Lock, Users, MessageCircle, Send, Inbox, DollarSign } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import apiClient from '../../utils/apiClient';
import { formatEcuadorDate } from '../../utils/ecuadorTime';

// ⭐ Dashboard CRM (pedido de Angelo, 2026-08-05: "un nuevo módulo... con
// las pocas gráficas referentes a las funciones actuales que tenemos,
// similar al tablero de GHL"). Mismo gate de acceso que Conversaciones/
// Pipeline/Workflows (hasCrmAccess) -- no es un producto aparte, es la
// pantalla de entrada del mismo addon CRM. Un solo fetch a GET
// /api/pos/crm/dashboard-stats (ver backend) trae todo lo que esta
// pantalla necesita.

const TEMPERATURE_LABELS = { frio: 'Frío', tibio: 'Tibio', caliente: 'Caliente' };
const TEMPERATURE_COLORS = { frio: '#3b82f6', tibio: '#f97316', caliente: '#ef4444' };

function KpiCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );
}

// ⭐ messagesByDay llega como filas sueltas {day, direction, count} (una
// fila por día+dirección) -- se pivotea acá a {day, entrantes, salientes}
// para que LineChart pueda dibujar las dos series. Se rellenan los últimos
// 14 días aunque no haya mensajes ese día (count 0), para que el eje X no
// salte fechas y la tendencia se lea bien incluso en negocios con poco
// volumen todavía.
function buildMessagesSeries(rows) {
  const byDay = {};
  (rows || []).forEach((r) => {
    const key = r.day;
    if (!byDay[key]) byDay[key] = { day: key, entrantes: 0, salientes: 0 };
    if (r.direction === 'inbound') byDay[key].entrantes = Number(r.count);
    else byDay[key].salientes = Number(r.count);
  });
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push(byDay[key] || { day: key, entrantes: 0, salientes: 0 });
  }
  return days.map(d => ({ ...d, label: formatEcuadorDate(d.day) }));
}

const DashboardCRM = () => {
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [stats, setStats] = useState(null);
  // ⭐ Embudo seleccionado para las tarjetas de valor. 'all' suma todos, pero
  // el default es el primer pipeline: mezclar embudos distintos en un solo
  // total suele ser justo lo que NO se quiere mirar.
  const [selectedPipelineId, setSelectedPipelineId] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadAccess = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/pos/crm/access');
      setHasAccess(Boolean(res.data?.hasCrmAccess));
    } catch (err) {
      setHasAccess(false);
    } finally {
      setLoadingAccess(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/pos/crm/dashboard-stats');
      setStats(res.data);
      // Si hay un solo embudo se selecciona solo: obligar a elegir cuando no
      // hay nada que elegir es fricción sin ganancia.
      const pl = res.data?.pipelines || [];
      if (pl.length === 1) setSelectedPipelineId(String(pl[0].id));
    } catch (err) { /* silencioso: se reintenta al refrescar */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { if (hasAccess) loadStats(); }, [hasAccess, loadStats]);

  if (loadingAccess) {
    return <div className="p-8 text-center text-gray-500">Cargando...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Lock className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Dashboard CRM no está incluido en tu plan</h2>
        <p className="text-gray-500 max-w-md mb-4">
          Este módulo viene incluido desde el plan Premium, o se puede contratar aparte sobre tu plan actual. Contacta a soporte para activarlo.
        </p>
      </div>
    );
  }

  if (loading && !stats) {
    return <div className="p-8 text-center text-gray-500">Cargando estadísticas...</div>;
  }

  const messagesSeries = buildMessagesSeries(stats?.messagesByDay);
  const temperatureData = (stats?.contactsByTemperature || []).map(t => ({
    name: TEMPERATURE_LABELS[t.temperature] || t.temperature,
    value: Number(t.count),
    color: TEMPERATURE_COLORS[t.temperature] || '#9ca3af',
  }));
  // ⭐ Valor del pipeline (2026-08-08). El selector filtra por pipeline en vez
  // de mostrar un total global: sumar embudos distintos ("Ventas" + "Post
  // venta", por ejemplo) da un número que no significa nada. Se filtra en
  // memoria sobre los datos que ya trajo dashboard-stats, así que cambiar de
  // embudo es instantáneo y no puede desincronizarse con las gráficas.
  const allStages = stats?.contactsByStage || [];
  const pipelines = stats?.pipelines || [];
  const stagesOfPipeline = selectedPipelineId === 'all'
    ? allStages
    : allStages.filter(s => String(s.pipeline_id) === String(selectedPipelineId));

  const openStages = stagesOfPipeline.filter(s => !Number(s.is_won));
  const wonStages = stagesOfPipeline.filter(s => Number(s.is_won));
  const sumValue = (rows) => rows.reduce((acc, s) => acc + Number(s.total_value || 0), 0);
  const sumCount = (rows) => rows.reduce((acc, s) => acc + Number(s.count || 0), 0);

  const openValue = sumValue(openStages);
  const wonValue = sumValue(wonStages);
  const openCount = sumCount(openStages);
  const wonCount = sumCount(wonStages);
  const money = (n) => '$' + Number(n || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const stageData = stagesOfPipeline
    .filter(s => Number(s.count) > 0)
    .map(s => ({
      name: (selectedPipelineId === 'all' && s.pipeline_name) ? `${s.stage_name} (${s.pipeline_name})` : s.stage_name,
      count: Number(s.count),
      value: Number(s.total_value || 0),
      color: s.color || '#3b82f6'
    }));
  const tagData = (stats?.contactsByTag || [])
    .filter(t => Number(t.count) > 0)
    .map(t => ({ name: t.name, count: Number(t.count), color: t.color || '#3b82f6' }));

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full">
      <div>
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-blue-600" /> Dashboard CRM
        </h2>
        <p className="text-sm text-gray-500">Vista general de contactos, conversaciones y pipeline.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Contactos totales" value={stats?.totalContacts ?? 0} tone="bg-blue-50 text-blue-600" />
        <KpiCard icon={MessageCircle} label="Conversaciones abiertas" value={stats?.openConversations ?? 0} tone="bg-green-50 text-green-600" />
        <KpiCard icon={Inbox} label="Conversaciones totales" value={stats?.totalConversations ?? 0} tone="bg-purple-50 text-purple-600" />
        <KpiCard icon={Send} label="Mensajes hoy" value={stats?.messagesToday ?? 0} tone="bg-orange-50 text-orange-600" />
      </div>

      {/* ⭐ Valor del embudo (2026-08-08). Abierto y ganado van SEPARADOS a
          propósito: uno es proyección y el otro es cierre, y sumarlos da un
          número que infla el pipeline y no sirve para decidir nada. */}
      {pipelines.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="text-sm font-bold text-gray-700 flex items-center gap-1.5">
              <DollarSign className="w-4 h-4 text-emerald-600" /> Valor del embudo
            </h3>
            <select
              value={selectedPipelineId}
              onChange={(e) => setSelectedPipelineId(e.target.value)}
              className="px-2 py-1 border rounded-lg text-xs text-gray-700"
            >
              {pipelines.length > 1 && <option value="all">Todos los embudos</option>}
              {pipelines.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
              <p className="text-xs text-gray-500">En proceso</p>
              <p className="text-2xl font-bold text-blue-700">{money(openValue)}</p>
              <p className="text-[11px] text-gray-500">{openCount} {openCount === 1 ? 'prospecto' : 'prospectos'} abiertos</p>
            </div>
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
              <p className="text-xs text-gray-500">Ganado</p>
              <p className="text-2xl font-bold text-emerald-700">{money(wonValue)}</p>
              <p className="text-[11px] text-gray-500">
                {wonStages.length === 0
                  ? 'Marca una etapa como "cierre ganado" en Pipeline'
                  : `${wonCount} ${wonCount === 1 ? 'cierre' : 'cierres'}`}
              </p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
              <p className="text-xs text-gray-500">Ticket promedio abierto</p>
              <p className="text-2xl font-bold text-gray-700">
                {money(openCount ? openValue / openCount : 0)}
              </p>
              <p className="text-[11px] text-gray-500">Por prospecto en proceso</p>
            </div>
          </div>

          {openValue === 0 && wonValue === 0 && (
            <p className="text-[11px] text-gray-400 mt-3">
              Todavía no hay valores cargados. Ponle un valor a cada etapa desde Pipeline
              y todos los prospectos que caigan ahí lo heredan automáticamente.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mensajes por día (14 días) */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Mensajes últimos 14 días</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={messagesSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="entrantes" stroke="#3b82f6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="salientes" stroke="#22c55e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Contactos por temperatura */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Contactos por temperatura</h3>
          {temperatureData.every(t => t.value === 0) ? (
            <p className="text-xs text-gray-400 text-center py-16">Todavía no hay contactos.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={temperatureData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {temperatureData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Contactos por etapa */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Contactos por etapa</h3>
          {stageData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-16">Todavía no hay contactos en ninguna etapa.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, stageData.length * 32)}>
              <BarChart data={stageData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                {/* El tooltip muestra también cuánto vale la etapa: la barra
                    responde "cuántos hay", el valor responde "cuánto pesan",
                    que casi nunca es lo mismo. */}
                <Tooltip formatter={(v, name, item) => [`${v} contactos · ${money(item?.payload?.value)}`, 'Etapa']} />
                <Bar dataKey="count">
                  {stageData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Contactos por etiqueta */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Contactos por etiqueta</h3>
          {tagData.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-16">Todavía no hay contactos etiquetados.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, tagData.length * 32)}>
              <BarChart data={tagData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                <Tooltip />
                <Bar dataKey="count">
                  {tagData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardCRM;
